import React, { useState, useEffect, useRef } from 'react';
import { MoreHorizontal, X } from 'lucide-react';
import * as Icons from 'lucide-react';
import shortcutService, { Shortcut } from '../../services/shortcutService';

interface ShortcutBarProps {
  onNavigate: (path: string) => void;
}

export const ShortcutBar: React.FC<ShortcutBarProps> = ({ onNavigate }) => {
  const [shortcuts, setShortcuts] = useState<Shortcut[]>([]);
  const [visibleShortcuts, setVisibleShortcuts] = useState<Shortcut[]>([]);
  const [overflowShortcuts, setOverflowShortcuts] = useState<Shortcut[]>([]);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);

  // Load shortcuts
  useEffect(() => {
    loadShortcuts();

    const handleShortcutsChanged = () => {
      loadShortcuts();
    };

    window.addEventListener('shortcuts-changed', handleShortcutsChanged);
    return () => window.removeEventListener('shortcuts-changed', handleShortcutsChanged);
  }, []);

  // Handle resize to recalculate visible shortcuts
  useEffect(() => {
    const handleResize = () => {
      calculateVisibleShortcuts();
    };

    window.addEventListener('resize', handleResize);
    calculateVisibleShortcuts();

    return () => window.removeEventListener('resize', handleResize);
  }, [shortcuts]);

  // Close overflow menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        overflowMenuRef.current &&
        !overflowMenuRef.current.contains(event.target as Node)
      ) {
        setShowOverflowMenu(false);
      }
    };

    if (showOverflowMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOverflowMenu]);

  const loadShortcuts = () => {
    const loadedShortcuts = shortcutService.getShortcuts();
    setShortcuts(loadedShortcuts);
  };

  const calculateVisibleShortcuts = () => {
    if (!containerRef.current || shortcuts.length === 0) {
      setVisibleShortcuts(shortcuts);
      setOverflowShortcuts([]);
      return;
    }

    // Estimate available width (container width - padding - more button space)
    const containerWidth = containerRef.current.offsetWidth;
    const iconSize = 40; // Width of each icon including margin
    const moreButtonSize = 45;
    const availableWidth = containerWidth - moreButtonSize - 20; // 20px padding

    const maxVisibleIcons = Math.floor(availableWidth / iconSize);

    if (shortcuts.length <= maxVisibleIcons) {
      setVisibleShortcuts(shortcuts);
      setOverflowShortcuts([]);
    } else {
      setVisibleShortcuts(shortcuts.slice(0, maxVisibleIcons));
      setOverflowShortcuts(shortcuts.slice(maxVisibleIcons));
    }
  };

  const handleShortcutClick = (shortcut: Shortcut) => {
    onNavigate(shortcut.path);
    setShowOverflowMenu(false);
  };

  const handleRemoveShortcut = (shortcut: Shortcut, e: React.MouseEvent) => {
    e.stopPropagation();
    shortcutService.removeShortcut(shortcut.id);
  };

  const getIcon = (iconName: string) => {
    const IconComponent = (Icons as any)[iconName] || Icons.Package;
    return IconComponent;
  };

  const getGroupedShortcuts = (shortcutList: Shortcut[]) => {
    const grouped: { [key: string]: Shortcut[] } = {};

    shortcutList.forEach(shortcut => {
      if (!grouped[shortcut.group]) {
        grouped[shortcut.group] = [];
      }
      grouped[shortcut.group].push(shortcut);
    });

    return grouped;
  };

  const getGroupDisplayName = (groupId: string): string => {
    const groupNames: { [key: string]: string } = {
      'plukk-pakk': 'Plukk & Pakk',
      'varemottak': 'Varemottak',
      'lager': 'Lager',
      'historikk': 'Historikk',
      'vareplassering': 'Vareplassering',
      'transaksjoner': 'Transaksjoner',
      'verktoy': 'Verktøy',
      'grunndata': 'Grunndata',
      'lagerstilling': 'Lagerstilling'
    };
    return groupNames[groupId] || groupId;
  };

  if (shortcuts.length === 0) {
    return null; // Don't render if no shortcuts
  }

  const groupedVisible = getGroupedShortcuts(visibleShortcuts);
  const groupOrder = ['plukk-pakk', 'varemottak', 'lager'];

  return (
    <div
      ref={containerRef}
      className="flex items-center gap-1 justify-center"
    >
      {/* Visible shortcuts with group separators */}
      {groupOrder.map((groupId, groupIndex) => {
        const groupShortcuts = groupedVisible[groupId];
        if (!groupShortcuts || groupShortcuts.length === 0) return null;

        return (
          <React.Fragment key={groupId}>
            {groupIndex > 0 && <div className="h-6 w-px bg-gray-300 mx-1" />}
            {groupShortcuts.map((shortcut) => {
              const IconComponent = getIcon(shortcut.icon);
              return (
                <div
                  key={shortcut.id}
                  className="relative group"
                >
                  <button
                    onClick={() => handleShortcutClick(shortcut)}
                    className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
                    title={shortcut.title}
                  >
                    <IconComponent size={20} className="text-gray-700" />
                  </button>

                  {/* Remove button on hover - outside main button to avoid nesting */}
                  <button
                    onClick={(e) => handleRemoveShortcut(shortcut, e)}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    title="Fjern snarvei"
                  >
                    <X size={10} />
                  </button>
                </div>
              );
            })}
          </React.Fragment>
        );
      })}

      {/* Overflow menu button */}
      {overflowShortcuts.length > 0 && (
        <div className="relative" ref={overflowMenuRef}>
          <button
            onClick={() => setShowOverflowMenu(!showOverflowMenu)}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded transition-colors ml-1"
            title="Flere snarveier"
          >
            <MoreHorizontal size={20} className="text-gray-700" />
          </button>

          {/* Overflow dropdown menu */}
          {showOverflowMenu && (
            <div className="absolute top-full right-0 sm:left-0 sm:right-auto mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-[200px] max-w-[90vw] z-50">
              {Object.entries(getGroupedShortcuts(overflowShortcuts)).map(([groupId, groupShortcuts], index) => (
                <div key={groupId}>
                  {index > 0 && <div className="border-t border-gray-200 my-2" />}
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">
                    {getGroupDisplayName(groupId)}
                  </div>
                  {groupShortcuts.map((shortcut) => {
                    const IconComponent = getIcon(shortcut.icon);
                    return (
                      <div
                        key={shortcut.id}
                        className="relative group"
                      >
                        <button
                          onClick={() => handleShortcutClick(shortcut)}
                          className="w-full px-3 py-3 sm:py-2 min-h-[44px] hover:bg-gray-100 active:bg-gray-200 transition-colors flex items-center gap-3 text-left pr-10"
                        >
                          <IconComponent size={18} className="text-gray-700 flex-shrink-0" />
                          <span className="text-sm text-gray-900 truncate">{shortcut.title}</span>
                        </button>
                        {/* Remove button - positioned as sibling to avoid nesting */}
                        <button
                          onClick={(e) => handleRemoveShortcut(shortcut, e)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1.5 sm:p-1 opacity-0 group-hover:opacity-100 transition-opacity min-w-[28px] min-h-[28px] sm:min-w-0 sm:min-h-0 flex items-center justify-center z-10"
                          title="Fjern snarvei"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
